<template>
    <!--
        Two different illustrations, not two sizes of one (Asana O2-52):
        the .png set is 260x250, the .jpg set is 720x506. The small one was
        being used at every width, including a 567px slot — a quarter of the
        resolution needed on a 2x screen, which is what "very low-res" meant.

        The larger source is only offered when it EXISTS: four neighborhoods
        have a png and no jpg, and <picture> does not fall back to its <img>
        when a <source> 404s, it shows a broken image. See
        app/utils/neighborhoodMapArt.ts.
    -->
    <picture>
        <source v-if="hasLargeArt && hasSmallArt" :srcset="jpg" :media="`(min-width: ${TABLET}px)`" />
        <img :src="png" :alt="alt" :loading="loading" />
    </picture>
</template>

<script setup>
    import { NEIGHBORHOOD_MAP_JPGS, NEIGHBORHOOD_MAP_PNGS, MAP_PLACEHOLDER } from '~/utils/neighborhoodMapArt';
    import { neighborhoodSnakeCase } from '~/composables/helpers/neighborhoodSnakeCase';

    // Matches $tablet in assets/styles/vars.scss. Hard-coded because a media
    // attribute cannot read a SCSS variable; keep the two in step.
    const TABLET = 768;

    const props = defineProps({
        name: { type: String, required: true },
        alt: { type: String, required: false, default: 'Neighborhood map image' },
        loading: { type: String, required: false, default: 'lazy' },
    });

    const slug = computed(() => neighborhoodSnakeCase(props.name || ''));
    const hasSmallArt = computed(() => NEIGHBORHOOD_MAP_PNGS.has(slug.value));
    const hasLargeArt = computed(() => NEIGHBORHOOD_MAP_JPGS.has(slug.value));

    // Two neighborhoods have neither drawing and rendered as broken images.
    // The placeholder covers both breakpoints, so no <source> is offered with it.
    const png = computed(() => (hasSmallArt.value
        ? `/img/pages/nearby-neighborhoods/map-${slug.value}.png`
        : MAP_PLACEHOLDER));
    const jpg = computed(() => `/img/pages/neighborhoods/map-${slug.value}.jpg`);
</script>
